import React, { useEffect, useMemo, useState } from 'react';
import {
  Row,
  Col,
  Card,
  Button,
  Table,
  Typography,
  Space,
  Input,
  Modal,
  Tooltip,
  Tag,
  Popconfirm,
  Spin,
  message
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  SearchOutlined,
  ClearOutlined,
  UploadOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { showError, showSuccess, showWarning } from '@components/notification';
import moment from 'moment';
import { usePermission } from '@contexts/Auth/UsePermission';
import {
  apiGetFavoriteFilters,
  apiDeleteFavoriteFilter,
  apiExportFavoriteFilterItems
} from '@services/ManageCustomers/FavoriteFilterServices';
import { apiGetAllCustomer } from '@services/ManageCustomers/CustomerServices';
import { apiGetDevicesInformationV2 } from '@services/ManageDevices/DeviceServices';
import FavoriteFilterForm from '@components/FavoriteFilter/FavoriteFilterForm';
import ModalImportFavoriteFilter from './ModalImportFavoriteFilter';

const { Title, Text } = Typography;

/* ================================================================ */
/* Modal: chi tiết danh sách các mục được gán */
/* ================================================================ */
const ModalFilterDetail = ({ open, onClose, filterData }) => {
  const [detailData, setDetailData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);

  const handleExport = async () => {
    try {
      setLoadingExport(true);
      const res = await apiExportFavoriteFilterItems(filterData.id);
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.setAttribute('download', `Bo_loc_${filterData.filter_name || 'danh_sach'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      showError('Có lỗi xảy ra khi xuất dữ liệu');
    } finally {
      setLoadingExport(false);
    }
  };

  useEffect(() => {
    if (!open || !filterData?.item_ids?.length) {
      setDetailData([]);
      return;
    }

    setLoading(true);
    const fetchDetails = async () => {
      try {
        if (filterData.filter_type === 'customer') {
          // Fetch customers by codes
          const res = await apiGetAllCustomer({
            page: 1,
            size: 1000,
            codes: filterData.item_ids.join(',')
          });
          const rows = res.data?.data || [];
          // Filter by codes in case server doesn't support codes param
          const filtered = rows.filter((r) => filterData.item_ids.includes(r.code));
          setDetailData(filtered.length ? filtered : rows);
        } else if (filterData.filter_type === 'device') {
          // Fetch devices by serial numbers
          const res = await apiGetDevicesInformationV2({
            page: 1,
            size: 1000,
            serial_nos: filterData.item_ids.join(',')
          });
          const rows = res.data?.data || [];
          // Filter by serial_no in case server doesn't support serial_nos param
          const filtered = rows.filter((r) => filterData.item_ids.includes(r.serial_no));
          setDetailData(filtered.length ? filtered : rows);
        }
      } catch (err) {
        console.error('Error fetching details:', err);
        message.error('Lỗi khi tải dữ liệu chi tiết');
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [open, filterData]);

  const customerColumns = [
    {
      title: 'STT',
      width: 55,
      align: 'center',
      render: (_, __, i) => i + 1
    },
    {
      title: 'Mã KH',
      dataIndex: 'code',
      width: 120,
      render: (v) => <Tag color="blue">{v}</Tag>
    },
    {
      title: 'Tên khách hàng',
      dataIndex: 'name',
      width: 200
    },
    {
      title: 'Chi nhánh',
      dataIndex: 'branch',
      width: 110,
      render: (v) => v || '-'
    },
    {
      title: 'Tổ quản lý',
      dataIndex: 'manage_group',
      width: 110,
      render: (v) => v || '-'
    },
    {
      title: 'Địa chỉ',
      dataIndex: 'address',
      width: 220,
      render: (v) => v || '-'
    }
  ];

  const deviceColumns = [
    {
      title: 'STT',
      width: 55,
      align: 'center',
      render: (_, __, i) => i + 1
    },
    {
      title: 'Serial Number',
      dataIndex: 'serial_no',
      width: 150,
      render: (v) => <Tag color="blue">{v}</Tag>
    },
    {
      title: 'IMEI',
      dataIndex: 'imei',
      width: 150
    },
    {
      title: 'Loại thiết bị',
      dataIndex: 'device_category',
      width: 120,
      render: (v) => v || '-'
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      width: 100,
      render: (v) => {
        const statusMap = { IN_USE: 'Đang sử dụng', STORED: 'Lưu kho' };
        return <Tag>{statusMap[v] || v || '-'}</Tag>;
      }
    }
  ];

  const columns = filterData?.filter_type === 'customer' ? customerColumns : deviceColumns;

  return (
    <Modal
      title={`Chi tiết ${filterData?.filter_type === 'customer' ? 'Khách hàng' : 'Thiết bị'} - ${
        filterData?.filter_name
      }`}
      open={open}
      onCancel={onClose}
      maskClosable={false}
      width={1000}
      footer={[
        <Button key="close" type="primary" onClick={onClose}>
          Đóng
        </Button>
      ]}
      destroyOnClose>
      <Spin spinning={loading}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px'
          }}>
          <Text strong>
            Tổng cộng: {detailData.length}{' '}
            {filterData?.filter_type === 'customer' ? 'khách hàng' : 'thiết bị'}
          </Text>
          <Button
            icon={<DownloadOutlined />}
            loading={loadingExport}
            onClick={handleExport}
            style={{ borderColor: '#0284c7', color: '#0284c7' }}>
            Xuất Excel
          </Button>
        </div>
        <Table
          rowKey={filterData?.filter_type === 'customer' ? 'code' : 'serial_no'}
          size="small"
          columns={columns}
          dataSource={detailData}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          scroll={{ x: 'max-content', y: 400 }}
        />
      </Spin>
    </Modal>
  );
};

export default function List() {
  const [filterList, setFilterList] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  const [visibleForm, setVisibleForm] = useState(false);
  const [editingFilter, setEditingFilter] = useState(null);
  const [visibleDetail, setVisibleDetail] = useState(false);
  const [detailFilter, setDetailFilter] = useState(null);
  const [visibleImport, setVisibleImport] = useState(false);
  const { favoriteFilter } = usePermission();

  const fetchData = async () => {
    try {
      setIsFetching(true);
      const res = await apiGetFavoriteFilters();
      if (res.data?.success) {
        const data = res.data?.data || [];

        // Filter by search text
        const filtered = data.filter(
          (item) =>
            item.filter_name.toLowerCase().includes(search.toLowerCase()) ||
            item.filter_description.toLowerCase().includes(search.toLowerCase())
        );

        // Sort by favorite first, then by updated_time
        const sorted = filtered.sort((a, b) => {
          if (a.is_favorite === b.is_favorite) {
            return new Date(b.updated_time) - new Date(a.updated_time);
          }
          return a.is_favorite ? -1 : 1;
        });

        // Calculate pagination
        const total = sorted.length;
        const start = (currentPage - 1) * pageSize;
        const end = start + pageSize;
        const paginatedData = sorted.slice(start, end);

        setFilterList(
          paginatedData.map((item, index) => ({
            ...item,
            stt: start + index + 1
          }))
        );
        setTotalItems(total);
      } else {
        showError(res.data?.message || 'Lỗi tải danh sách bộ lọc');
      }
    } catch (err) {
      console.error('Error fetching filters:', err);
      showError('Lỗi khi tải danh sách bộ lọc');
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize, search]);

  const handleChangePage = (page) => {
    setCurrentPage(page);
  };

  const handleChangePageSize = (current, size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const handleResetSearch = () => {
    setSearch('');
    setCurrentPage(1);
  };

  const handleAddFilter = () => {
    // if (!customer.create) {
    //   showWarning({ message: 'Bạn không có quyền tạo bộ lọc' });
    //   return;
    // }
    setEditingFilter(null);
    setVisibleForm(true);
  };

  const handleEditFilter = (record) => {
    // if (!customer.update) {
    //   showWarning({ message: 'Bạn không có quyền chỉnh sửa bộ lọc' });
    //   return;
    // }
    setEditingFilter(record);
    setVisibleForm(true);
  };

  const handleDeleteFilter = async (id) => {
    try {
      const res = await apiDeleteFavoriteFilter(id);
      if (res.data?.success) {
        showSuccess({ message: 'Xóa bộ lọc thành công' });
        fetchData();
      } else {
        showError(res.data?.message || 'Xóa bộ lọc thất bại');
      }
    } catch (err) {
      console.error('Error deleting filter:', err);
      showError('Lỗi khi xóa bộ lọc');
    }
  };

  const handleFormSuccess = () => {
    fetchData();
  };

  const handleViewDetails = (record) => {
    setDetailFilter(record);
    setVisibleDetail(true);
  };

  const columns = useMemo(
    () => [
      {
        title: 'STT',
        dataIndex: 'stt',
        width: 80,
        fixed: 'left',
        align: 'center'
      },
      {
        title: 'Tên bộ lọc',
        dataIndex: 'filter_name',
        fixed: 'left',
        width: 250,
        render: (text) => <strong>{text}</strong>
      },
      {
        title: 'Mô tả',
        dataIndex: 'filter_description',
        width: 250,
        render: (text) => <span>{text || '-'}</span>
      },
      {
        title: 'Loại lọc',
        dataIndex: 'filter_type',
        width: 120,
        render: (text) => {
          const displayMap = {
            customer: 'Khách hàng',
            device: 'Thiết bị'
          };
          return <Tag>{displayMap[text] || text}</Tag>;
        }
      },
      {
        title: 'Mục đã gán',
        dataIndex: 'item_ids',
        width: 200,
        render: (items, record) => {
          if (!items || items.length === 0) {
            return <span style={{ color: '#999' }}>-</span>;
          }
          const displayItems = items.slice(0, 2);
          const hasMore = items.length > 2;
          return (
            <div>
              <Space direction="vertical" size={0}>
                {displayItems.map((item, idx) => (
                  <span key={idx} style={{ fontSize: '12px' }}>
                    {item}
                  </span>
                ))}
                {hasMore && (
                  <Button
                    type="link"
                    size="small"
                    onClick={() => handleViewDetails(record)}
                    style={{ padding: 0, height: 'auto' }}>
                    +{items.length - 2} mục khác
                  </Button>
                )}
              </Space>
              {!hasMore && items.length > 0 && (
                <Button
                  type="link"
                  size="small"
                  onClick={() => handleViewDetails(record)}
                  style={{ padding: 0, height: 'auto', display: 'block', marginTop: 4 }}>
                  Xem chi tiết
                </Button>
              )}
            </div>
          );
        }
      },
      {
        title: 'Cập nhật lúc',
        dataIndex: 'updated_time',
        width: 150,
        render: (timestamp) => moment(timestamp).format('DD/MM/YYYY HH:mm')
      },
      {
        title: 'Hành động',
        fixed: 'right',
        width: 150,
        align: 'center',
        render: (_, record) => (
          <Space size="small">
            <Tooltip title="Chỉnh sửa">
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEditFilter(record)}
              />
            </Tooltip>
            <Popconfirm
              title="Xác nhận xóa"
              description="Bạn có chắc chắn muốn xóa bộ lọc này không?"
              onConfirm={() => handleDeleteFilter(record.id)}
              okText="Có"
              cancelText="Không">
              <Tooltip title="Xóa">
                <Button type="text" size="small" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          </Space>
        )
      }
    ],
    []
  );

  return (
    <div className="dashboard">
      {/* Nút tạo và title ở trên */}
      <Row justify="space-between" align="middle" style={{ marginBottom: '16px' }}>
        <Col>
          <Space>
            {/* <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/customers')}>
              Quay lại
            </Button> */}
            <Typography.Title level={4} style={{ margin: 0 }}>
              Bộ lọc theo dõi
            </Typography.Title>
          </Space>
        </Col>
        <Col>
          <Space>
            {favoriteFilter.import && (
              <Button
                icon={<UploadOutlined />}
                onClick={() => setVisibleImport(true)}
                style={{ borderColor: '#0284c7', color: '#0284c7' }}>
                Import Excel
              </Button>
            )}
            {favoriteFilter.create && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddFilter}
                style={{
                  backgroundColor: '#0284c7',
                  borderColor: '#0284c7'
                }}>
                Tạo bộ lọc
              </Button>
            )}
          </Space>
        </Col>
      </Row>

      {/* Card filter và search */}
      <Card bodyStyle={{ padding: 8 }}>
        <Row gutter={[8, 8]}>
          <Col xs={24} md={16}>
            <Input
              value={search}
              onChange={handleSearchChange}
              placeholder="Tìm kiếm tên hoặc mô tả..."
              onPressEnter={() => {}}
              style={{ width: '100%', height: 36 }}
            />
          </Col>
          <Col xs={24} md={8}>
            <Space style={{ width: '100%' }}>
              <Button icon={<ClearOutlined />} onClick={handleResetSearch} style={{ height: 36 }}>
                Xoá tìm kiếm
              </Button>
              <Button
                icon={<SearchOutlined />}
                type="primary"
                onClick={() => {
                  setCurrentPage(1);
                }}
                style={{
                  height: 36,
                  backgroundColor: '#0284c7',
                  borderColor: '#0284c7'
                }}>
                Tìm kiếm
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Bảng danh sách */}
      <Table
        bordered
        loading={isFetching}
        dataSource={filterList}
        columns={columns}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: totalItems,
          showSizeChanger: true,
          onChange: handleChangePage,
          onShowSizeChange: handleChangePageSize,
          showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bộ lọc`
        }}
        scroll={{ x: 1000 }}
        rowKey="id"
      />

      {/* Form modal để tạo/chỉnh sửa */}
      <FavoriteFilterForm
        visible={visibleForm}
        onClose={() => setVisibleForm(false)}
        onSuccess={handleFormSuccess}
        editingFilter={editingFilter}
      />

      {/* Modal chi tiết danh sách các mục được gán */}
      <ModalFilterDetail
        open={visibleDetail}
        onClose={() => setVisibleDetail(false)}
        filterData={detailFilter}
      />

      {/* Modal import từ Excel */}
      <ModalImportFavoriteFilter
        visible={visibleImport}
        onCancel={() => setVisibleImport(false)}
        onSuccess={() => {
          setVisibleImport(false);
          fetchData();
        }}
      />
    </div>
  );
}
